import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useVisits } from '@/hooks/use-visits';
import { AddProductModal } from '@/components/visits/AddProductModal';
import { AddNoteModal } from '@/components/visits/AddNoteModal';
import { EditVisitModal } from '@/components/visits/EditVisitModal';
import { VisitPhotoService } from '@/lib/storage';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { customerService } from '@/lib/packages';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowLeft, 
  Clock, 
  MapPin, 
  Camera, 
  Package, 
  FileText, 
  Plus,
  User,
  Calendar,
  Edit,
  CheckCircle,
  PlayCircle,
  Link,
  Trash2,
  Loader2,
  Search
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

// Helper function to calculate duration for face recognition visits
const calculateVisitDuration = (visit: any, isManual: boolean, isFaceRecognition: boolean) => {
  // Manual visits don't have duration tracking
  if (isManual) {
    return 'Manuel ziyaret - süre takibi yok';
  }

  // Face recognition visits should show duration
  if (isFaceRecognition) {
    // Visit has ended: use ended_at, or duration_sec, or exited_at (mapped to ended_at in toVisitRecord)
    const hasEnded = visit.ended_at || (visit.duration_sec != null && visit.duration_sec > 0);
    if (hasEnded) {
      if (visit.duration_sec != null && visit.duration_sec > 0) {
        // Prefer stored duration when available (from visit_duration_sec)
        const hours = Math.floor(visit.duration_sec / 3600);
        const minutes = Math.floor((visit.duration_sec % 3600) / 60);
        return `${hours}s ${minutes}d`;
      }
      if (visit.ended_at) {
        // Calculate from start to end
        const start = new Date(visit.started_at);
        const end = new Date(visit.ended_at);
        const diff = end.getTime() - start.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}s ${minutes}d`;
      }
    }
    // Still in progress
    const now = new Date();
    const start = new Date(visit.started_at);
    const diff = now.getTime() - start.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}s ${minutes}d (devam ediyor)`;
  }

  return 'Bilinmiyor';
};

const VisitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, companyId } = useAuth();
  const { toast } = useToast();
  const { 
    getVisit, 
    getVisitProducts, 
    getVisitPhotos, 
    getVisitNotes, 
    endVisit, 
    linkVisitToCustomer, 
    uploadPhoto, 
    addProductToVisit,
    addNoteToVisit,
    updateVisit,
    deleteVisit,
    isEndingVisit, 
    isLinkingVisit, 
    isUploadingPhoto,
    isAddingProduct,
    isAddingNote,
    isUpdatingVisit,
    isDeletingVisit,
    getTemporaryVisitData, 
    isTemporaryVisit, 
    getVisitDisplayName, 
    isManualVisit, 
    isFaceRecognitionVisit, 
    getVisitSourceText 
  } = useVisits();
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Customer search states for link dialog
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  
  // Fetch visit data
  const { data: visit, isLoading: isLoadingVisit } = getVisit(id || '');
  const { data: visitProducts = [], isLoading: isLoadingProducts } = getVisitProducts(id || '');
  const { data: visitPhotos = [], isLoading: isLoadingPhotos } = getVisitPhotos(id || '');
  const { data: visitNotes = [], isLoading: isLoadingNotes } = getVisitNotes(id || '');
  
  const getCustomerName = (customer: any) => {
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri';
  };

  // Debounced search for customers - MUST be before early returns
  const searchCustomers = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setCustomers([]);
      return;
    }

    setLoadingCustomers(true);
    try {
      const results = await customerService.searchCustomers(companyId, searchTerm, 20);
      setCustomers(results);
    } catch (error) {
      console.error('Error searching customers:', error);
      toast({
        title: "Hata",
        description: "Müşteri arama sırasında hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoadingCustomers(false);
    }
  }, [companyId, toast]);

  // Effect for customer search - MUST be before early returns
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);
  
  if (isLoadingVisit) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!visit) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <h2 className="text-xl font-semibold">Ziyaret bulunamadı</h2>
          <Button onClick={() => navigate('/customers')}>
            Müşteri Listesine Dön
          </Button>
        </div>
      </Layout>
    );
  }

  const formatTime = (timeString: string) => {
    if (!timeString) return 'Bilinmiyor';
    return new Date(timeString).toLocaleTimeString('tr-TR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timeString: string) => {
    if (!timeString) return 'Bilinmiyor';
    return new Date(timeString).toLocaleDateString('tr-TR');
  };

  const formatDateTime = (timeString: string) => {
    if (!timeString) return 'Bilinmiyor';
    return new Date(timeString).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEndVisit = () => {
    if (visit?.id) {
      endVisit(visit.id);
    }
  };

  const handleLinkToCustomer = (customerId: string) => {
    if (visit?.id) {
      linkVisitToCustomer({ visitId: visit.id, customerId }, {
        onSuccess: () => {
          setShowLinkDialog(false);
          setCustomerSearch('');
          setCustomers([]);
          setShowCustomerResults(false);
        }
      });
    }
  };

  const handleOpenLinkDialog = () => {
    setCustomerSearch('');
    setCustomers([]);
    setShowCustomerResults(false);
    setShowLinkDialog(true);
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && visit?.id) {
      uploadPhoto({ visitId: visit.id, file });
      // Reset the input
      event.target.value = '';
    }
  };

  const handlePhotoButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleAddProduct = (productId: string, notes?: string) => {
    if (visit?.id) {
      addProductToVisit({ visitId: visit.id, productId, notes });
    }
  };

  const handleAddNote = (content: string, noteType: 'general' | 'follow_up' | 'important' = 'general') => {
    if (visit?.id) {
      addNoteToVisit({ 
        visitId: visit.id, 
        content, 
        authorUserId: user?.id,
        noteType 
      });
    }
  };

  const handleUpdateVisit = (updates: { notes?: string; visit_type?: string; location?: string }) => {
    if (visit?.id) {
      updateVisit({ visitId: visit.id, updates });
      setShowEditModal(false);
    }
  };

  const handleDeleteVisit = () => {
    if (visit?.id) {
      deleteVisit(visit.id);
      navigate('/customers');
    }
  };

  const normalizedStatus = String(visit?.status || '').toLowerCase();
  const isOpen = !visit?.ended_at && (normalizedStatus === 'in_progress' || normalizedStatus === 'pending_label' || normalizedStatus === '');
  const isTemp = visit ? isTemporaryVisit(visit) : false;
  const displayName = visit ? getVisitDisplayName(visit) : 'Unknown';
  const tempData = visit ? getTemporaryVisitData(visit.id) : null;
  const isManual = visit ? isManualVisit(visit) : false;
  const isFaceRecognition = visit ? isFaceRecognitionVisit(visit) : false;
  const sourceText = visit ? getVisitSourceText(visit) : 'Bilinmiyor';

  // Calculate duration based on visit type
  const duration = visit ? calculateVisitDuration(visit, isManual, isFaceRecognition) : 'Bilinmiyor';

  // Products from visit_products table
  const products = visitProducts || [];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/customers')}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Geri
        </Button>

        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="w-12 h-12 border-2 border-white/20">
                  <AvatarFallback className="bg-accent/20 text-accent font-semibold">
                    {displayName.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-xl font-bold">{displayName}</h1>
                    {visit.customer_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => navigate(`/customers/${visit.customer_id}`)}
                      >
                        Müşteri Detayı
                      </Button>
                    )}
                    {isTemp && (
                      <Badge variant="secondary">Geçici</Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(visit.started_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {visit.location || 'Bilinmiyor'}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {sourceText}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge 
                  variant={isOpen ? 'default' : 'secondary'}
                  className={isOpen ? 'bg-success/20 text-success' : ''}
                >
                  {isOpen ? (
                    <>
                      <PlayCircle className="w-3 h-3 mr-1" />
                      Açık
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Kapalı
                    </>
                  )}
                </Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Başlangıç</p>
                  <p className="font-semibold">
                    {formatDateTime(visit.started_at)}
                  </p>
                </div>
              </div>

              {(visit.ended_at || (visit.duration_sec != null && visit.duration_sec > 0)) && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bitiş</p>
                    <p className="font-semibold">
                      {visit.ended_at
                        ? formatDateTime(visit.ended_at)
                        : formatDateTime(new Date(new Date(visit.started_at).getTime() + (visit.duration_sec || 0) * 1000).toISOString())}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Süre</p>
                  <p className="font-semibold">{duration}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Products */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Ürünler & Hizmetler
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-1"
                  onClick={() => setShowAddProductModal(true)}
                >
                  <Plus className="w-4 h-4" />
                  Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingProducts ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                  <p>Ürünler yükleniyor...</p>
                </div>
              ) : products.length > 0 ? (
                <div className="space-y-3">
                  {products.map((product: any) => (
                    <div key={product.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex-1">
                        <h4 className="font-medium">{product.product?.name || 'Ürün adı'}</h4>
                        <p className="text-sm text-muted-foreground">{product.product?.category || 'Kategori'}</p>
                        {product.notes && (
                          <p className="text-sm text-muted-foreground italic mt-1">{product.notes}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{product.product?.item_code || 'Kod'}</p>
                        <p className="text-sm text-muted-foreground">
                          ₺{product.product?.price?.toLocaleString('tr-TR') || '0'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(product.noted_at || product.created_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 pt-3 border-t">
                    <p className="text-sm text-muted-foreground text-center">
                      {products.length} ürün görüntülendi
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">Henüz ürün eklenmedi</p>
                  <Button 
                    variant="outline" 
                    className="gap-2"
                    onClick={() => setShowAddProductModal(true)}
                  >
                    <Plus className="w-4 h-4" />
                    İlk Ürünü Ekle
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Photos */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Fotoğraflar
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-1"
                  onClick={handlePhotoButtonClick}
                  disabled={isUploadingPhoto}
                >
                  {isUploadingPhoto ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {isUploadingPhoto ? 'Yükleniyor...' : 'Ekle'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingPhotos ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                  <p>Fotoğraflar yükleniyor...</p>
                </div>
              ) : visitPhotos.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {visitPhotos.map((photo: any) => {
                    // Handle both visit_photos table format and direct media_links format
                    const mediaData = photo.media_link || photo;
                    const photoPath = mediaData.path;
                    const bucket = mediaData.bucket || 'visit-photos';
                    const timestamp = photo.taken_at || photo.created_at;
                    
                    return (
                      <div key={photo.id} className="relative group">
                        <div className="aspect-square rounded-lg bg-muted overflow-hidden">
                          <img 
                            src={VisitPhotoService.getPhotoUrl(photoPath)} 
                            alt={photo.caption || "Ziyaret fotoğrafı"}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/placeholder.svg';
                            }}
                          />
                        </div>
                        <div className="mt-1">
                          <p className="text-xs text-muted-foreground">
                            {formatTime(timestamp)}
                          </p>
                          {photo.caption && (
                            <p className="text-xs text-foreground truncate">
                              {photo.caption}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Camera className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">Henüz fotoğraf eklenmedi</p>
                  <Button 
                    variant="outline" 
                    className="gap-2"
                    onClick={handlePhotoButtonClick}
                    disabled={isUploadingPhoto}
                  >
                    {isUploadingPhoto ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                    {isUploadingPhoto ? 'Yükleniyor...' : 'Fotoğraf Ekle'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Notes */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Notlar
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowAddNoteModal(true)}
              >
                <Edit className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingNotes ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                <p>Notlar yükleniyor...</p>
              </div>
            ) : visit?.notes || tempData?.note || visitNotes.length > 0 ? (
              <div className="space-y-4">
                {visit?.notes && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">Ziyaret Notu</p>
                    <p className="whitespace-pre-wrap">{visit.notes}</p>
                  </div>
                )}
                {tempData?.note && tempData.note !== visit?.notes && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">Geçici Not</p>
                    <p className="whitespace-pre-wrap">{tempData.note}</p>
                  </div>
                )}
                {visitNotes.map((note: any) => (
                  <div key={note.id} className="p-3 rounded-lg bg-muted/50">
                    <div className="flex justify-between items-start mb-2">
                      <p className="text-sm text-muted-foreground">
                        {note.author?.full_name || 'Bilinmeyen'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatTime(note.created_at)}
                      </p>
                    </div>
                    <p className="whitespace-pre-wrap">{note.content}</p>
                    {note.note_type && note.note_type !== 'general' && (
                      <p className="text-xs text-accent mt-1 capitalize">
                        {note.note_type.replace('_', ' ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">Henüz not eklenmedi</p>
                <Button 
                  variant="outline" 
                  className="gap-2"
                  onClick={() => setShowAddNoteModal(true)}
                >
                  <Plus className="w-4 h-4" />
                  Not Ekle
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3">
              {isOpen && (
                <Button 
                  className="gap-2" 
                  onClick={handleEndVisit}
                  disabled={isEndingVisit}
                >
                  {isEndingVisit ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {isEndingVisit ? 'Bitiriliyor...' : 'Ziyareti Bitir'}
                </Button>
              )}
              
              {isTemp && (
                <Button 
                  variant="outline" 
                  className="gap-2"
                  onClick={handleOpenLinkDialog}
                  disabled={isLinkingVisit}
                >
                  {isLinkingVisit ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link className="w-4 h-4" />
                  )}
                  {isLinkingVisit ? 'Bağlanıyor...' : 'Müşteriye Bağla'}
                </Button>
              )}
              
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => setShowEditModal(true)}
              >
                <Edit className="w-4 h-4" />
                Düzenle
              </Button>
              
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => setShowAddProductModal(true)}
              >
                <Plus className="w-4 h-4" />
                Ürün Ekle
              </Button>
              
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={handlePhotoButtonClick}
                disabled={isUploadingPhoto}
              >
                {isUploadingPhoto ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
                {isUploadingPhoto ? 'Yükleniyor...' : 'Fotoğraf Ekle'}
              </Button>
              
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => setShowAddNoteModal(true)}
              >
                <FileText className="w-4 h-4" />
                Not Ekle
              </Button>
              
              {!isOpen && (
                <Button 
                  variant="destructive" 
                  className="gap-2 ml-auto"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeletingVisit}
                >
                  {isDeletingVisit ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {isDeletingVisit ? 'Siliniyor...' : 'Sil'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Link to Customer Dialog */}
        <Dialog 
          open={showLinkDialog} 
          onOpenChange={(open) => {
            setShowLinkDialog(open);
            if (!open) {
              setCustomerSearch('');
              setCustomers([]);
              setShowCustomerResults(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Müşteri Seç ve Bağla</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Bu ziyaret kaydını hangi müşteriye bağlamak istiyorsunuz?
              </p>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Müşteri ara..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerResults(e.target.value.length > 0);
                    if (e.target.value === '') {
                      setCustomers([]);
                    }
                  }}
                  onFocus={() => setShowCustomerResults(customerSearch.length > 0)}
                  onBlur={() => setTimeout(() => setShowCustomerResults(false), 150)}
                  className="pl-10"
                />
                
                {showCustomerResults && (
                  <div className="absolute top-full mt-1 w-full bg-popover border border-border rounded-md shadow-lg z-10 max-h-48 overflow-y-auto backdrop-blur-sm">
                    {loadingCustomers ? (
                      <div className="p-3 text-muted-foreground text-center">
                        <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                        Müşteriler aranıyor...
                      </div>
                    ) : customers.length > 0 ? (
                      customers.map((customer) => (
                        <div
                          key={customer.id}
                          className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                          onClick={() => {
                            handleLinkToCustomer(customer.id);
                            setShowCustomerResults(false);
                            setCustomerSearch('');
                          }}
                        >
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-accent/20 text-accent text-xs">
                              {getCustomerName(customer).split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{getCustomerName(customer)}</p>
                            <p className="text-sm text-muted-foreground">{customer.email || customer.phone}</p>
                          </div>
                        </div>
                      ))
                    ) : customerSearch.length >= 2 ? (
                      <div className="p-3 text-muted-foreground text-center">
                        Müşteri bulunamadı
                      </div>
                    ) : (
                      <div className="p-3 text-muted-foreground text-center">
                        En az 2 karakter girin
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowLinkDialog(false)}
                  className="flex-1"
                >
                  İptal
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Product Modal */}
        <AddProductModal
          open={showAddProductModal}
          onOpenChange={setShowAddProductModal}
          onProductAdd={handleAddProduct}
          isAdding={isAddingProduct}
        />

        {/* Add Note Modal */}
        <AddNoteModal
          open={showAddNoteModal}
          onOpenChange={setShowAddNoteModal}
          onNoteAdd={handleAddNote}
          isAdding={isAddingNote}
        />

        {/* Edit Visit Modal */}
        <EditVisitModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          visit={visit}
          onVisitUpdate={handleUpdateVisit}
          isUpdating={isUpdatingVisit}
        />

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background border rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-semibold mb-2">Ziyareti Sil</h3>
              <p className="text-muted-foreground mb-4">
                Bu ziyareti silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              </p>
              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeletingVisit}
                >
                  İptal
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteVisit}
                  disabled={isDeletingVisit}
                >
                  {isDeletingVisit ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Siliniyor...
                    </>
                  ) : (
                    'Sil'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Hidden file input for photo upload */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handlePhotoUpload}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </div>
    </Layout>
  );
};

export default VisitDetail;